/**
 * La centralita: el softphone de Amazon Connect empotrado en el CRM.
 *
 * Amazon Connect no expone un softphone como componente: expone el CCP (Contact
 * Control Panel) como una página suya que se carga en un iframe, y la librería
 * `amazon-connect-streams` abre un canal con ese iframe para leer el estado y
 * dar órdenes. De ahí salen las dos reglas que gobiernan este fichero.
 *
 * La primera es que **el iframe no puede desmontarse nunca**. Es el que sostiene
 * la conexión WebRTC: si React lo quita del DOM porque el usuario navegó a otra
 * pantalla, la llamada en curso se corta sin aviso ni forma de recuperarla. Por
 * eso el CCP no vive dentro de ninguna ruta, sino en un contenedor anclado al
 * layout raíz, y la pestaña Centralita se limita a colocarse encima de él.
 *
 * La segunda es que **initCCP solo puede llamarse una vez por pestaña del
 * navegador**. Streams no es idempotente y React 18 en StrictMode monta cada
 * efecto dos veces en desarrollo, así que sin el cerrojo de aquí abajo el CCP
 * aparece en blanco en local y funciona en producción, que es la peor
 * combinación posible para depurar.
 *
 * El estado se guarda fuera de React —un objeto y un conjunto de suscriptores—
 * porque los eventos de Streams llegan desde el iframe en cualquier momento,
 * también mientras React está renderizando.
 */
import 'amazon-connect-streams';

/**
 * Trozos de Streams que los tipos de la comunidad no cubren.
 *
 * `@types/amazon-connect-streams` va por su cuenta y se queda corto frente a la
 * 2.29. Se declara aquí solo lo que se usa, en un sitio, en vez de salpicar
 * `as any` por el fichero.
 */
type NucleoStreams = {
  getEventBus(): { subscribe(evento: string, fn: (...args: unknown[]) => void): unknown };
  iframeRefreshTimeout: number | null;
  iframeRefreshAttempt: number;
};
const nucleo = () => connect.core as unknown as NucleoStreams;

/**
 * Los nombres de evento se toman de las constantes de Streams, no a mano.
 * `makeEnum` conserva el literal original —ACK_TIMEOUT vale 'ack_timeout', no
 * 'ackTimeout'—, y escribirlos a ojo hace que la suscripción no salte nunca y
 * que además falle en silencio, que es lo peor de los dos mundos.
 */
const EVENTO = connect.EventType as unknown as Record<string, string>;

/** Instancia `sereno` en Londres. El CCP se sirve desde el dominio de Connect. */
const CCP_URL = 'https://sereno.my.connect.aws/ccp-v2/';
// `landat` hace que, tras identificarse, la ventana vuelva al CCP y este avise
// al iframe de que ya hay sesión.
const LOGIN_URL = 'https://sereno.my.connect.aws/login?landat=%2Fccp-v2%2F';
const REGION = 'eu-west-2';

export type EstadoLlamada =
  | 'sin-iniciar'   // aún no se ha cargado el CCP
  | 'cargando'      // iframe puesto, esperando a que Streams dé señales
  | 'sin-sesion'    // el CCP pide iniciar sesión en Amazon Connect
  | 'libre'         // agente conectado, sin llamada
  | 'marcando'      // llamada saliente en curso, aún no descolgada
  | 'sonando'       // llamada entrante ofrecida al agente
  | 'hablando'      // llamada conectada
  | 'tras-llamada'; // ACW: colgó pero el hueco sigue ocupado hasta cerrar

export interface Centralita {
  estado: EstadoLlamada;
  /** Nombre del estado del agente en Connect ('Available', 'Offline', …). */
  estadoAgente: string | null;
  /** Los estados que el agente puede seleccionar, para el desplegable. */
  estadosDisponibles: string[];
  /** El número del otro extremo, si hay llamada. */
  numero: string | null;
  /** Identificador del contacto en Connect; sirve para casarlo con el CRM. */
  contactId: string | null;
  /** Momento en que se conectó la llamada, para el cronómetro. */
  conectadaEn: number | null;
  /** Último error legible, si algo falló. */
  error: string | null;
  /** A quién estamos llamando según el CRM, cuando la llamada la lanzamos nosotros. */
  destino: { nombre: string | null; alojamiento: string | null } | null;
}

const inicial: Centralita = {
  estado: 'sin-iniciar',
  estadoAgente: null,
  estadosDisponibles: [],
  numero: null,
  contactId: null,
  conectadaEn: null,
  error: null,
  destino: null,
};

let estado: Centralita = inicial;
const suscriptores = new Set<() => void>();

/** getSnapshot de useSyncExternalStore exige identidad estable entre renders. */
export function leer(): Centralita {
  return estado;
}

export function suscribir(fn: () => void): () => void {
  suscriptores.add(fn);
  return () => suscriptores.delete(fn);
}

function cambiar(parche: Partial<Centralita>) {
  estado = { ...estado, ...parche };
  suscriptores.forEach((fn) => fn());
}

// ── Ciclo de vida ────────────────────────────────────────────────────────────

/**
 * El cerrojo contra el doble initCCP. Va a nivel de módulo, no de componente,
 * porque StrictMode desmonta y remonta conservando el módulo.
 */
let iniciado = false;
let agente: connect.Agent | null = null;

/**
 * Arranca el CCP dentro del contenedor dado. Idempotente: la segunda llamada
 * no hace nada, que es justo lo que hace falta en StrictMode.
 */
export function iniciar(contenedor: HTMLElement): void {
  if (iniciado) return;
  iniciado = true;
  cambiar({ estado: 'cargando', error: null });

  try {
    connect.core.initCCP(contenedor, {
      ccpUrl: CCP_URL,
      region: REGION,
      // El popup automático de Streams no funciona, y no por cómo lo llamemos:
      // initCCP no abre la ventana, programa un temporizador de 5 s que dispara
      // ACK_TIMEOUT, y el window.open acaba saliendo desde dentro de un callback
      // de navigator.locks. Para entonces hace rato que se agotó la activación
      // por gesto del usuario, así que el navegador lo bloquea siempre. Se apaga
      // y la abre `iniciarSesion()` desde el clic, que es donde sí se permite.
      loginPopup: false,
      softphone: {
        // Sin esto el audio no suena dentro del iframe: Connect asume que el
        // softphone vive en su propia ventana y no en la de otro.
        allowFramedSoftphone: true,
      },
      pageOptions: {
        // Que el agente pueda elegir micrófono y auriculares sin salir del CRM.
        enableAudioDeviceSettings: true,
        enablePhoneTypeSettings: false,
      },
    });
  } catch (e) {
    iniciado = false;
    cambiar({ estado: 'sin-iniciar', error: e instanceof Error ? e.message : 'No se pudo cargar la centralita' });
    return;
  }

  connect.agent((a) => {
    agente = a;
    refrescarAgente(a);
    a.onRefresh(refrescarAgente);
    // Sin sesión, Streams avisa por aquí en vez de por un error de initCCP.
    a.onError(() => cambiar({ error: 'La centralita ha dado un error. Recarga la página.' }));
  });

  connect.contact(engancharContacto);

  // Con `loginPopup: false`, Streams deja de reautenticar solo. Y no solo al
  // arrancar: los tres avisos —arranque, caducidad y cierre de sesión— acaban
  // en el mismo `authenticate()`, que no hace nada cuando el popup está
  // apagado. Así que hay que atender los tres, o la centralita se muere sola a
  // las doce horas sin decir por qué.
  const bus = nucleo().getEventBus();
  //   ACK_TIMEOUT  · el iframe no contesta: no hay sesión todavía
  bus.subscribe(EVENTO.ACK_TIMEOUT, () => cambiar({ estado: 'sin-sesion', error: null }));
  //   AUTH_FAIL    · la sesión de 12 h ha caducado
  connect.core.onAuthFail(() => cambiar({ estado: 'sin-sesion', error: null }));
  //   TERMINATED   · el agente ha cerrado sesión en Connect
  bus.subscribe(EVENTO.TERMINATED, () => cambiar({ estado: 'sin-sesion', error: null }));

  connect.core.onAccessDenied(() =>
    cambiar({ estado: 'sin-sesion', error: 'Esta cuenta no tiene acceso a la centralita.' }),
  );

  // Streams reintenta cargar el iframe diez veces y luego se rinde. El problema
  // es que al rendirse deja su temporizador con un id no nulo, y el guardián de
  // `authenticate()` solo rearma si es null: sin tocar eso a mano, el CCP queda
  // muerto hasta recargar la página. Le pasa a cualquiera que tarde más de un
  // minuto y medio en identificarse, que con un gestor de contraseñas es normal.
  bus.subscribe(EVENTO.IFRAME_RETRIES_EXHAUSTED, () => {
    nucleo().iframeRefreshTimeout = null;
    nucleo().iframeRefreshAttempt = 0;
    cambiar({ estado: 'sin-sesion', error: null });
  });

  // Navegar dentro del CRM no toca el iframe, pero recargar, cerrar la pestaña
  // o salir a otra web sí: la llamada se corta sin remedio. Mientras haya una
  // viva, el navegador pregunta antes. El ACW no cuenta: ahí ya no hay voz.
  window.addEventListener('beforeunload', (e) => {
    if (estado.estado !== 'marcando' && estado.estado !== 'sonando' && estado.estado !== 'hablando') return;
    e.preventDefault();
    e.returnValue = '';
  });
}

function refrescarAgente(a: connect.Agent) {
  let nombre: string | null = null;
  let disponibles: string[] = [];
  try {
    nombre = a.getStatus().name;
    disponibles = a.getAgentStates().map((s) => s.name);
  } catch {
    // Durante el arranque getStatus puede no estar listo todavía.
  }
  agente = a;
  const enLlamada = a.getContacts(connect.ContactType.VOICE).length > 0;
  cambiar({
    estadoAgente: nombre,
    estadosDisponibles: disponibles,
    // Solo se manda el estado global si no hay una llamada mandando ella.
    ...(enLlamada ? {} : { estado: 'libre' as EstadoLlamada, numero: null, contactId: null, conectadaEn: null }),
  });
}

/**
 * Sigue una llamada de principio a fin.
 *
 * `agent.connect()` no devuelve el contactId ni acepta atributos, así que el
 * único punto donde se puede casar una llamada saliente con la ficha del CRM
 * es aquí: cuando Connect anuncia el contacto, se le pega el destino que
 * habíamos apuntado al marcar.
 */
function engancharContacto(contacto: connect.Contact) {
  const numeroDe = () => {
    try {
      return contacto.getInitialConnection()?.getEndpoint()?.phoneNumber ?? null;
    } catch {
      return null;
    }
  };

  const entrante = contacto.isInbound();
  cambiar({
    contactId: contacto.getContactId(),
    numero: numeroDe(),
    estado: entrante ? 'sonando' : 'marcando',
  });

  contacto.onConnecting(() => cambiar({ estado: entrante ? 'sonando' : 'marcando', numero: numeroDe() }));
  contacto.onConnected(() => cambiar({ estado: 'hablando', conectadaEn: Date.now(), numero: numeroDe() }));
  contacto.onEnded(() => cambiar({ estado: 'tras-llamada', conectadaEn: null }));
  // onDestroy llega cuando el contacto desaparece del agente, ya cerrado el ACW.
  contacto.onDestroy(() =>
    cambiar({ estado: 'libre', numero: null, contactId: null, conectadaEn: null, destino: null }),
  );
  contacto.onMissed(() => cambiar({ estado: 'libre', numero: null, contactId: null, destino: null }));
}

// ── Acciones ─────────────────────────────────────────────────────────────────

/** Un número en E.164 tal y como lo quiere Connect: '+34600112233'. */
export function normalizar(numero: string): string | null {
  const limpio = numero.replace(/[^\d+]/g, '');
  if (limpio.startsWith('+')) return limpio.length >= 8 ? limpio : null;
  const digitos = limpio.replace(/\D/g, '');
  if (digitos.length === 9) return `+34${digitos}`;            // móvil o fijo español
  if (digitos.length === 11 && digitos.startsWith('34')) return `+${digitos}`;
  return digitos.length >= 8 ? `+${digitos}` : null;
}

export function puedeLlamar(): boolean {
  return estado.estado === 'libre' && Boolean(agente);
}

/**
 * Marca un número. `destino` es lo que sabe el CRM de quién hay al otro lado,
 * y se guarda para poder enseñarlo mientras suena y para casar la llamada
 * después.
 */
export function llamar(
  numero: string,
  destino?: { nombre: string | null; alojamiento: string | null },
): Promise<void> {
  return new Promise((resolve, reject) => {
    const e164 = normalizar(numero);
    if (!e164) return reject(new Error(`No parece un número de teléfono: ${numero}`));
    if (!agente) return reject(new Error('La centralita todavía no está lista'));

    cambiar({ error: null, destino: destino ?? null, numero: e164, estado: 'marcando' });
    agente.connect(connect.Endpoint.byPhoneNumber(e164), {
      success: () => resolve(),
      failure: (err) => {
        cambiar({ estado: 'libre', numero: null, destino: null, error: mensajeDeFallo(err) });
        reject(new Error(mensajeDeFallo(err)));
      },
    });
  });
}

/**
 * Traduce el error de Streams a algo que se pueda leer en pantalla.
 *
 * Los códigos vienen del servidor, no de la librería: `agent.connect()` no
 * valida nada en el navegador salvo que el número no sea nulo. Por eso no se
 * filtra por estado del agente —marcar se puede desde cualquiera— y lo que se
 * mira son los códigos CTI reales que puede devolver Connect.
 */
function mensajeDeFallo(err: unknown): string {
  const texto = typeof err === 'string' ? err : JSON.stringify(err ?? '');
  if (/BadEndpoint|Destination|Endpoint|invalid/i.test(texto)) {
    return 'Ese número no se puede marcar. Comprueba el prefijo del país.';
  }
  if (/AccessDenied|Unauthorized/i.test(texto)) {
    return 'Esta cuenta no tiene permiso para hacer llamadas salientes.';
  }
  if (/InvalidState/i.test(texto)) {
    return 'La centralita no está lista para llamar. Prueba a recargar la página.';
  }
  if (/QuotaExceeded/i.test(texto)) return 'Hay demasiadas llamadas a la vez. Inténtalo en un momento.';
  return 'No se pudo iniciar la llamada.';
}

export function colgar(): void {
  const contactos = agente?.getContacts(connect.ContactType.VOICE) ?? [];
  contactos.forEach((c) => {
    const conexion = c.getAgentConnection();
    if (conexion) conexion.destroy({ success: () => {}, failure: () => {} });
  });
}

export function aceptar(): void {
  const contacto = agente?.getContacts(connect.ContactType.VOICE)[0];
  contacto?.accept({ success: () => {}, failure: () => {} });
}

/**
 * Cierra el trabajo-tras-llamada.
 *
 * Sin esto el agente se queda en ACW con su único hueco de voz ocupado y no
 * puede volver a llamar. El CCP nativo trae su botón, pero como aquí el iframe
 * puede estar oculto, hace falta uno propio.
 */
export function cerrarTrabajo(): void {
  const contacto = agente?.getContacts(connect.ContactType.VOICE)[0];
  if (contacto) contacto.clear({ success: () => {}, failure: () => {} });
  else cambiar({ estado: 'libre', numero: null, contactId: null, destino: null });
}

export function cambiarEstadoAgente(nombre: string): void {
  const destino = agente?.getAgentStates().find((s) => s.name === nombre);
  if (destino && agente) agente.setState(destino, { success: () => {}, failure: () => {} });
}

export function estaIniciada(): boolean {
  return iniciado;
}

/** La dirección donde el agente se identifica. La abre un enlace, no un script. */
export const URL_ACCESO = LOGIN_URL;

/**
 * Vuelve a intentar la conexión después de que el agente se haya identificado.
 *
 * Hace falta un empujón manual porque Streams se rinde tras diez intentos y, al
 * rendirse, deja su temporizador con un identificador que no es nulo. El
 * guardián de `authenticate()` solo rearma cuando es nulo, así que sin poner
 * estos dos valores a cero el CCP se queda muerto hasta recargar la página
 * entera —le pasa a cualquiera que tarde más de minuto y medio en teclear su
 * contraseña, que con un gestor de contraseñas o un segundo factor es lo normal.
 */
export function reintentarSesion(): void {
  cambiar({ estado: 'cargando', error: null });
  try {
    nucleo().iframeRefreshTimeout = null;
    nucleo().iframeRefreshAttempt = 0;
    (connect.core as unknown as { authenticate?: () => void }).authenticate?.();
  } catch {
    // Si no se puede rearmar, recargar la página siempre funciona.
    cambiar({ estado: 'sin-sesion', error: 'Recarga la página para entrar.' });
  }
}
